import { useCallback, useMemo } from 'react';

import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/utils';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { useCreateRovoExperienceSidebarController } from '@confluence/ai-rovo-button/entry-points/useCreateRovoExperienceSidebarController.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

type UseToggleConversationAssistantReturnType = {
	mode: ConversationAssistantUIModes | undefined;
	toggleConversationAssistant: (openChatMode?: 'sidebar' | 'mini-modal') => void;
	openConversationAssistant: (openChatMode?: 'sidebar' | 'mini-modal') => void;
	isConversationAssistantSidebarActive: boolean;
};

/**
 * Note: this only works if Rovo is enabled, look up RovoButton as an example
 * @returns toggleConversationAssistant which will open the given mode of the Conversation Assistant
 */
export const useToggleConversationAssistant = (): UseToggleConversationAssistantReturnType => {
	const rightSidebarContext = useRightSidebarContext();
	const { panelId } = rightSidebarContext.getCurrent() || {};
	const {
		mode,
		isMiniModalAvailable,
		toggleConversationAssistantUIMode,
		openConversationAssistantUIMode,
	} = useRovoExperienceAPIStore();
	const isConversationAssistantSidebarActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID;
	const sidebarApi = useCreateRovoExperienceSidebarController();

	const toggleConversationAssistant = useCallback(
		(openChatMode: 'sidebar' | 'mini-modal' = 'sidebar') => {
			toggleConversationAssistantUIMode({
				targetMode:
					openChatMode === 'mini-modal' && isMiniModalAvailable
						? ConversationAssistantUIModes.MINI_MODAL
						: ConversationAssistantUIModes.SIDEBAR,
				sidebarApi,
			});
		},
		[toggleConversationAssistantUIMode, sidebarApi, isMiniModalAvailable],
	);

	const openConversationAssistant = useCallback(
		(openChatMode: 'sidebar' | 'mini-modal' = 'sidebar') => {
			openConversationAssistantUIMode({
				targetMode:
					openChatMode === 'mini-modal' && isMiniModalAvailable
						? ConversationAssistantUIModes.MINI_MODAL
						: ConversationAssistantUIModes.SIDEBAR,
				sidebarApi,
			});
		},
		[openConversationAssistantUIMode, sidebarApi, isMiniModalAvailable],
	);

	return useMemo(
		() => ({
			mode,
			toggleConversationAssistant,
			openConversationAssistant,
			isConversationAssistantSidebarActive,
		}),
		[
			mode,
			toggleConversationAssistant,
			openConversationAssistant,
			isConversationAssistantSidebarActive,
		],
	);
};
